/**
 * 묶음 합치기 창 — 합칠 후보 탭에서 체크한 제품들로 연다(현민 2026-10-26 · 백엔드 V장 "묶음 합치기 — 화면 다시").
 * 열면 고른 제품들을 서버에 묻는다(GET /merge-groups/preview) — 대표 라디오를 바꾸면 다시 묻는다(막힘은 대표와 그 제품 사이의 일).
 * 비교표 — 제품 = 열, 칸 = 줄(제품 상세를 열 때 한 번 받는다). 대표와 다른 값은 옅게 + 취소선(합치면 사라질 값, 현민 2026-10-26).
 * 칸 고르기(2026-10-26 밤) — 고를 수 있는 줄(검색어 · 메모, 대표가 미연결이면 디스크립션 · 카테고리도)은 칸을 누르면 그 제품 값이 남는다(초록 테두리). 대표를 바꾸면 고른 칸은 비운다.
 * 맨 아래 줄 = 들어갈 곳 — 대표 / "→ 대표로 · 실물 N장" / 막힌 이유(흐리게 · 합칠 목록에서 빠짐). [합치기 (n)] 은 막히지 않은 나머지만 한 트랜잭션으로.
 * 창은 document.body 에 붙였다가 닫으면 뗀다. 상태는 이 창 안에서만 산다. 둘씩 합치기 창(merge-dialog.ts)과 같은 틀.
 */
import { assert } from '../lib/assert';
import { escapeHtml } from '../lib/cells';
import { CATALOG_LINK_WORD, E_MERGE_PICK_FIELDS, fetchMergeGroupPreview, fetchProduct, mergeProductGroup, type EMergePickField, type MergeGroup, type MergeGroupMember, type MergePick, type ProductDetail } from '../api/product-api';

/** 창 하나의 상태 — 서버가 준 묶음(대표 · 줄마다 막힘) · 비교할 제품 상세(열 때 한 번) · 고른 칸(칸 → 제품 id, 없으면 대표 것) · 묻는 중 · 합치는 중 · 실패 글 */
type MergeGroupDialogState = { group: MergeGroup | null; details: ProductDetail[] | null; picks: Map<EMergePickField, string>; loading: boolean; merging: boolean; error: string };

/** 비교표 한 줄 — 칸 이름 · 그 칸을 상세에서 꺼내는 법 · 고를 수 있으면 그 칸(못 고르면 null) */
type MergeCompareField = { label: string; valueOf: (detail: ProductDetail) => string; pick: EMergePickField | null };

/** 대표가 카탈로그에 이어졌을 때 고를 수 있는 칸 — 디스크립션 · 카테고리는 카탈로그가 준다 */
const LINKED_PICK_FIELDS: readonly EMergePickField[] = ['search_text', 'notes'];

/** 비교할 칸 — 합치면 대표의 값만 남는다 */
const MERGE_COMPARE_FIELDS: readonly MergeCompareField[] = [
  { label: '파트넘버',   valueOf: (d) => d.option_part ?? '—', pick: null },
  { label: '디스크립션', valueOf: (d) => d.description ?? '', pick: 'description' },
  { label: '카테고리',   valueOf: (d) => d.spec_category ?? '—', pick: 'spec_category' },
  { label: '제조사',     valueOf: (d) => d.vendor ?? '—', pick: null },
  { label: '카탈로그',   valueOf: (d) => CATALOG_LINK_WORD[d.catalog], pick: null },
  { label: '스페어',     valueOf: (d) => d.spare_part ?? '—', pick: null },
  { label: '어셈블리',   valueOf: (d) => d.assembly_part ?? '—', pick: null },
  { label: '검색어',     valueOf: (d) => d.search_text ?? '', pick: 'search_text' },
  { label: '메모',       valueOf: (d) => d.notes ?? '', pick: 'notes' },
];

/** 창을 열고 사람의 답을 기다린다. 합쳤으면 남은 대표의 상세, 취소 · 바깥 · Esc 면 null */
export async function openMergeGroupDialog(memberIds: string[], suggestedKeepId: string): Promise<ProductDetail | null> {
  const host = document.createElement('div');
  const state: MergeGroupDialogState = { group: null, details: null, picks: new Map(), loading: true, merging: false, error: '' };
  const draw = (): void => { host.innerHTML = renderMergeGroupDialog(state); };
  document.body.appendChild(host);
  draw();

  return new Promise((resolve) => {
    function close(result: ProductDetail | null): void {
      document.removeEventListener('keydown', handleKey);
      host.remove();
      resolve(result);
    }
    function handleKey(event: KeyboardEvent): void {
      if (event.key === 'Escape' && !state.merging) close(null);
    }
    /** 대표로 묻기 — 처음 열 때와 라디오를 바꿀 때 */
    function loadPreview(keepId: string): void {
      state.loading = true;
      state.error = '';
      state.picks = new Map();   // 대표가 바뀌면 고른 칸은 새 대표 것으로 되돌린다
      draw();
      fetchMergeGroupPreview(keepId, memberIds)
        .then((group) => { state.group = inPickedOrder(group, memberIds); })
        .catch((e: unknown) => { state.error = e instanceof Error ? e.message : String(e); })
        .finally(() => { state.loading = false; draw(); });
    }
    async function handleMerge(): Promise<void> {
      const group = state.group;
      if (group === null) return;
      state.merging = true;
      state.error = '';
      draw();
      try {
        close(await mergeProductGroup(group.keep_id, mergeTargetsOf(group), mergePicksOf(group, state.picks)));
      } catch (e) {
        state.merging = false;   // 막힌 이유(409)는 서버 글 그대로 창에 남는다 — 하나라도 막히면 전부 되돌아갔다
        state.error = e instanceof Error ? e.message : String(e);
        draw();
      }
    }
    function handleClick(event: MouseEvent): void {
      const target = (event.target as HTMLElement).closest<HTMLElement>('[data-action]');
      if (target === null) return;
      if (target.dataset.action === 'merge-cancel' && !state.merging) close(null);
      if (target.dataset.action === 'merge-confirm') void handleMerge();
      if (target.dataset.action === 'merge-pick') handlePick(target);
    }
    /** 칸 누르기 — 그 제품 값을 남긴다. 대표 칸을 누르면 고른 것을 지운다 */
    function handlePick(target: HTMLElement): void {
      const group = state.group;
      if (group === null || state.loading || state.merging) return;
      const field = target.dataset.field as EMergePickField;
      const productId = target.dataset.id ?? '';
      assert(E_MERGE_PICK_FIELDS.includes(field) && productId !== '', `고를 칸은 data-field(고를 수 있는 칸) · data-id(제품 id)를 가진다 — renderCompareTable 이 붙였다: ${field} · ${productId}`);
      if (productId === group.keep_id) state.picks.delete(field);
      else state.picks.set(field, productId);
      draw();
    }
    function handleChange(event: Event): void {
      const target = event.target as HTMLElement;
      if (target.dataset.action !== 'merge-keep') return;
      const keepId = target.dataset.id ?? '';
      if (keepId !== '') loadPreview(keepId);
    }

    host.addEventListener('click', handleClick);
    host.addEventListener('change', handleChange);
    document.addEventListener('keydown', handleKey);
    loadPreview(suggestedKeepId);
    Promise.all(memberIds.map((id) => fetchProduct(id)))
      .then((details) => { state.details = details; })
      .catch((e: unknown) => { state.error = e instanceof Error ? e.message : String(e); })
      .finally(draw);
  });
}

/** 서버는 대표를 맨 앞에 싣는다 — 창은 고른 순서를 지켜 대표를 바꿔도 줄이 안 튀게 한다(현민 2026-10-26) */
function inPickedOrder(group: MergeGroup, memberIds: readonly string[]): MergeGroup {
  const members = [...group.members].sort((a, b) => memberIds.indexOf(a.id) - memberIds.indexOf(b.id));
  return { ...group, members };
}

/** 서버에 보낼 고른 칸 — 대표 아닌 제품을 고른 것만(대표 것은 그대로라 안 보낸다) */
function mergePicksOf(group: MergeGroup, picks: ReadonlyMap<EMergePickField, string>): MergePick[] {
  return [...picks].filter(([, productId]) => productId !== group.keep_id).map(([field, productId]) => ({ field, product_id: productId }));
}

/** 고를 수 있는 칸 — 대표가 카탈로그에 이어졌으면 검색어 · 메모만 */
function pickableFieldsOf(keep: ProductDetail): readonly EMergePickField[] {
  return keep.catalog === 'linked' ? LINKED_PICK_FIELDS : E_MERGE_PICK_FIELDS;
}

/** 합칠 제품 — 대표 아니고 막히지 않은 줄 */
export function mergeTargetsOf(group: MergeGroup): string[] {
  return group.members.filter((member) => member.id !== group.keep_id && member.blocked_reason === null).map((member) => member.id);
}

/** 창 전체 — 안내 · 비교표 · 실패 글 · [취소] [합치기 (n)] */
function renderMergeGroupDialog(state: MergeGroupDialogState): string {
  const group = state.group;
  const targets = group === null ? [] : mergeTargetsOf(group);
  const bCanMerge = !state.loading && !state.merging && state.details !== null && targets.length > 0;
  const body = group === null || state.details === null
    ? (state.error === '' ? '<p class="mg-note">불러오는 중…</p>' : '')
    : `<p class="mg-note">대표의 칸이 남고, 나머지 제품의 칸은 사라집니다. 실물 · 견적 줄은 대표로 옮겨집니다. 막힌 제품은 빠집니다.<br>밑줄 친 칸은 눌러서 다른 제품 값을 남길 수 있습니다.</p>
       ${renderCompareTable(group, state.details, state.picks, state.loading || state.merging)}`;
  return `
    <div class="mg-wrap" role="dialog" aria-label="제품 묶음 합치기">
      <div class="mg-overlay" data-action="merge-cancel"></div>
      <div class="mg-dialog mgg-dialog">
        <h3 class="mg-title">제품 합치기</h3>
        ${body}
        ${state.error === '' ? '' : `<p class="text-danger">${escapeHtml(state.error)}</p>`}
        <div class="mg-buttons">
          <button type="button" class="btn-ghost" data-action="merge-cancel" ${state.merging ? 'disabled' : ''}>취소</button>
          <button type="button" class="btn-primary" data-action="merge-confirm" ${bCanMerge ? '' : 'disabled'}>${state.merging ? '합치는 중…' : `합치기 (${targets.length})`}</button>
        </div>
      </div>
    </div>`;
}

/** 비교표 — 열 = 제품(고른 순서), 머리 = 대표 라디오 · 제품번호, 줄 = 칸들, 맨 아래 = 들어갈 곳. 남을 값과 다른 값은 사라질 값 */
function renderCompareTable(group: MergeGroup, details: readonly ProductDetail[], picks: ReadonlyMap<EMergePickField, string>, busy: boolean): string {
  const columns = group.members.map((member) => ({ member, detail: detailOf(details, member.id) }));
  const keep = detailOf(details, group.keep_id);
  const pickable = pickableFieldsOf(keep);
  const head = columns.map(({ member }) => `
          <th class="${columnClassOf(group, member)}">
            <label><input type="radio" name="mgg-keep" data-action="merge-keep" data-id="${escapeHtml(member.id)}" ${member.id === group.keep_id ? 'checked' : ''} ${busy ? 'disabled' : ''}> ${escapeHtml(member.hclt_number)}</label>
          </th>`).join('');
  const rows = MERGE_COMPARE_FIELDS.map((field) => {
    const pickField = field.pick !== null && pickable.includes(field.pick) ? field.pick : null;
    const keptId = pickField === null ? group.keep_id : picks.get(pickField) ?? group.keep_id;
    const keptValue = field.valueOf(detailOf(details, keptId));
    const cells = columns.map(({ member, detail }) => {
      const value = field.valueOf(detail);
      const bKept = member.id === keptId;
      const bGone = !bKept && value !== keptValue;
      const bPickable = pickField !== null && member.blocked_reason === null && !busy;
      const pickAttrs = bPickable ? ` data-action="merge-pick" data-field="${pickField}" data-id="${escapeHtml(member.id)}"` : '';
      const classes = [columnClassOf(group, member), bGone ? 'mgg-gone' : '', bPickable ? 'mgg-pickable' : '', pickField !== null && bKept ? 'mgg-picked' : ''].join(' ');
      const content = value === '' ? '<span class="mgg-empty">(비어 있음)</span>' : escapeHtml(value);
      return `<td class="${classes}"${pickAttrs}>${content}</td>`;
    }).join('');
    return `<tr><th>${escapeHtml(field.label)}</th>${cells}</tr>`;
  }).join('');
  const targetCells = columns.map(({ member }) => `<td class="${columnClassOf(group, member)} mgg-target">${targetWordOf(group, member)}</td>`).join('');
  return `
       <table class="pv-table mgg-table">
         <thead><tr><th>대표</th>${head}</tr></thead>
         <tbody>${rows}<tr><th>들어갈 곳</th>${targetCells}</tr></tbody>
       </table>`;
}

/** 열의 꼴 — 대표 열은 강조, 막힌 열은 흐리게 */
function columnClassOf(group: MergeGroup, member: MergeGroupMember): string {
  if (member.id === group.keep_id) return 'mgg-keep';
  return member.blocked_reason === null ? '' : 'mgg-blocked';
}

/** 들어갈 곳 — 대표 / → 대표로 · 실물 N장 / 빠짐 — 막힌 이유 */
function targetWordOf(group: MergeGroup, member: MergeGroupMember): string {
  if (member.id === group.keep_id) return '<b>대표</b>';
  if (member.blocked_reason !== null) return `빠짐 — ${escapeHtml(member.blocked_reason)}`;
  return member.counts.units > 0 ? `→ 대표로 · 실물 ${member.counts.units}장` : '→ 대표로';
}

function detailOf(details: readonly ProductDetail[], id: string): ProductDetail {
  const detail = details.find((item) => item.id === id);
  assert(detail !== undefined, `비교표의 제품은 열 때 받은 상세 안에 있다 — 고른 제품만 묻고 받았다: ${id}`);
  return detail;
}
